import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { get, post } from "../../services/api";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

function OptionsTab() {
  const [optionsData, setOptionsData] = useState([]);
  const [questionTypesData, setQuestionTypesData] = useState([]); // From GetQuestionTypes API
  const [allQuestions, setAllQuestions] = useState([]); // Flattened questions from questionTypes
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [filterQuestionType, setFilterQuestionType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  // New states
  const [questionTypes, setQuestionTypes] = useState([]); // For dropdown filter
  const [tableFilterQuestionType, setTableFilterQuestionType] = useState("");

  const animatedComponents = makeAnimated();

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    trigger,
  } = useForm({
    defaultValues: {
      id: null,
      questionId: "",
      optionText: "",
      optionValue: "",
      displayOrder: 1,
      scoreWeight: 1.0,
      isActive: true,
    },
  });

  // Get question types from API (which includes questions and default options)
  const getQuestionTypesData = useCallback(async () => {
    try {
      setQuestionsLoading(true);
      const response = await get(
        "QAForm/GetQuestionTypes?includeInactive=false"
      );

      let questionTypesArray = [];
      if (response && Array.isArray(response)) {
        questionTypesArray = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        questionTypesArray = response.data;
      } else if (response && response.isSuccess === false) {
        toast.error(response.message || "No data received");
        setQuestionTypesData([]);
        return;
      }

      setQuestionTypesData(questionTypesArray);

      // Create question types dropdown options
      const typeOptions = questionTypesArray.map((type) => ({
        value: type.code,
        label: type.name,
        data: type,
      }));

      // Add "All Types" option
      setQuestionTypes([
        { value: "", label: "All Types", data: null },
        ...typeOptions,
      ]);

      // Extract and flatten all questions from question types
      const allExtractedQuestions = [];
      questionTypesArray.forEach((type) => {
        if (type.question && Array.isArray(type.question)) {
          type.question.forEach((q) => {
            allExtractedQuestions.push({
              ...q,
              questionTypeCode: type.code,
              questionTypeName: type.name,
              questionTypeId: type.id,
              hasOptions: type.hasOptions,
              defaultOptions: type.defaultOptions || [],
              categoryName: q.category?.name || "Uncategorized",
            });
          });
        }
      });

      setAllQuestions(allExtractedQuestions);
      setFilteredQuestions(allExtractedQuestions);
    } catch (error) {
      console.error("Error fetching question types:", error);
      toast.error(error.message || "Failed to load data");
      setQuestionTypesData([]);
      setAllQuestions([]);
      setFilteredQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  // Get options data (custom options from API or default options from question types)
  const getOptionsData = useCallback(
    async (filter = {}) => {
      try {
        setLoading(true);

        // First try to get custom options from API
        try {
          const queryParams = new URLSearchParams();

          if (filter.questionId !== undefined) {
            queryParams.append("questionId", filter.questionId);
          } else if (selectedQuestionId) {
            queryParams.append("questionId", selectedQuestionId);
          }

          if (filter.questionType)
            queryParams.append("questionType", filter.questionType);
          if (filter.searchTerm)
            queryParams.append("searchTerm", filter.searchTerm);

          const queryString = queryParams.toString();
          const url = queryString
            ? `QAForm/options?${queryString}`
            : "QAForm/options";

          const response = await get(url);

          if (response && Array.isArray(response)) {
            setOptionsData(response);
          } else if (
            response &&
            response.data &&
            Array.isArray(response.data)
          ) {
            setOptionsData(response.data);
          } else {
            // If no custom options, show default options based on filter
            prepareDefaultOptions(filter);
          }
        } catch (apiError) {
          console.log(
            "Custom options API not available, showing default options"
          );
          prepareDefaultOptions(filter);
        }
      } catch (error) {
        console.error("Error fetching options:", error);
        prepareDefaultOptions(filter);
      } finally {
        setLoading(false);
      }
    },
    [selectedQuestionId, selectedQuestion, allQuestions]
  );

  // Prepare default options from question types
  const prepareDefaultOptions = (filter) => {
    let options = [];

    if (selectedQuestion) {
      // Show default options for selected question
      const questionType = questionTypesData.find(
        (qt) => qt.code === selectedQuestion.questionTypeCode
      );

      if (
        questionType?.defaultOptions &&
        questionType.defaultOptions.length > 0
      ) {
        options = questionType.defaultOptions.map((opt) => ({
          id: `default-${questionType.id}-${opt.optionValue}`,
          questionId: selectedQuestion.id,
          questionText: selectedQuestion.questionText,
          questionType: selectedQuestion.questionType,
          questionTypeCode: selectedQuestion.questionTypeCode,
          questionTypeName: selectedQuestion.questionTypeName,
          categoryName: selectedQuestion.categoryName,
          optionText: opt.optionText,
          optionValue: opt.optionValue,
          displayOrder: opt.displayOrder,
          scoreWeight: opt.scoreWeight || 1.0,
          isActive: true,
          isDefault: true,
          canEdit: questionType.isCustomizable,
        }));
      }
    } else {
      // Show all default options from all question types
      questionTypesData.forEach((type) => {
        if (type.defaultOptions && type.defaultOptions.length > 0) {
          // Find questions that use this question type
          const relatedQuestions = allQuestions.filter(
            (q) => q.questionTypeCode === type.code
          );

          if (relatedQuestions.length > 0) {
            type.defaultOptions.forEach((opt) => {
              relatedQuestions.forEach((question) => {
                options.push({
                  id: `default-${type.id}-${question.id}-${opt.optionValue}`,
                  questionId: question.id,
                  questionText: question.questionText,
                  questionType: question.questionType,
                  questionTypeCode: type.code,
                  questionTypeName: type.name,
                  categoryName: question.categoryName,
                  optionText: opt.optionText,
                  optionValue: opt.optionValue,
                  displayOrder: opt.displayOrder,
                  scoreWeight: opt.scoreWeight || 1.0,
                  isActive: true,
                  isDefault: true,
                  canEdit: type.isCustomizable,
                });
              });
            });
          }
        }
      });

      // Apply filters if any
      if (filter.questionType) {
        options = options.filter(
          (opt) => opt.questionTypeCode === filter.questionType
        );
      }

      if (filter.searchTerm) {
        const searchLower = filter.searchTerm.toLowerCase();
        options = options.filter(
          (opt) =>
            opt.questionText.toLowerCase().includes(searchLower) ||
            opt.optionText.toLowerCase().includes(searchLower) ||
            opt.optionValue.toLowerCase().includes(searchLower)
        );
      }
    }

    setOptionsData(options);
  };

  // Save/Update option
  const saveOption = async (data) => {
    setIsSubmitting(true);
    try {
      const userId = localStorage.getItem("userId") || "0";

      const apiData = {
        questionId: parseInt(data.questionId),
        optionText: data.optionText,
        optionValue: data.optionValue,
        displayOrder: parseInt(data.displayOrder),
        scoreWeight: parseFloat(data.scoreWeight),
        isActive: data.isActive,
        createdBy: userId,
      };

      let response;

      if (data.id && !data.id.toString().startsWith("default-")) {
        // Update existing custom option
        const updateData = {
          ...apiData,
          id: data.id,
        };
        response = await post(`QAForm/UpdateOption`, updateData);
      } else {
        // Create new custom option
        console.log(apiData);
        response = await post("QAForm/CreateOption", apiData);
      }

      if (
        response &&
        (response.isSuccess === true || response.status === "success")
      ) {
        toast.success(response.message || "Option saved successfully!");
        getOptionsData(); // Refresh the list
        resetForm();
      } else {
        toast.error(response?.message || "Failed to save option");
      }
    } catch (error) {
      console.error("Error saving option:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save option"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit option
  const handleEdit = async (row) => {
    try {
      // Try to get custom option from API
      try {
        const response = await get(`QAForm/GetOptionById?id=${row.id}`);

        const option = response?.data || response;

        if (option) {
          reset({
            id: option.id,
            questionId: option.questionId?.toString() || "",
            optionText: option.optionText,
            optionValue: option.optionValue,
            displayOrder: option.displayOrder,
            scoreWeight: parseFloat(option.scoreWeight),
            isActive: option.isActive,
          });

          // Find and set the selected question
          const question = allQuestions.find((q) => q.id === option.questionId);
          setSelectedQuestion(question);
          setSelectedQuestionId(option.questionId);
        } else {
          toast.error("Failed to load option data");
        }
      } catch (apiError) {
        // If API fails, edit the default option as if it were custom
        reset({
          id: row.original.id,
          questionId: row.original.questionId?.toString() || "",
          optionText: row.original.optionText,
          optionValue: row.original.optionValue,
          displayOrder: row.original.displayOrder,
          scoreWeight: parseFloat(row.original.scoreWeight),
          isActive: row.original.isActive,
        });

        const question = allQuestions.find(
          (q) => q.id === row.original.questionId
        );
        setSelectedQuestion(question);
        setSelectedQuestionId(row.original.questionId);
      }

      await trigger("questionId");
    } catch (error) {
      console.error("Error editing option:", error);
      toast.error(error.message || "Failed to load option data");
    }
  };

  // Delete option
  const handleDelete = async (rowId) => {
    // Check if it's a default option
    const option = optionsData.find((o) => o.id === rowId);
    if (option?.isDefault) {
      toast.error("Cannot delete default options. They can only be modified.");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this option?"
    );

    if (confirmDelete) {
      try {
        const response = await get(`QAForm/delete-options?id=${rowId}`);

        if (
          response &&
          (response.isSuccess === true || response.status === "success")
        ) {
          toast.success(response.message || "Option deleted successfully!");
          getOptionsData(); // Refresh the list
        } else {
          toast.error(response?.message || "Failed to delete option");
        }
      } catch (error) {
        console.error("Error deleting option:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete option"
        );
      }
    }
  };

  // Toggle status
  const handleToggleStatus = async (rowId) => {
    const option = optionsData.find((o) => o.id === rowId);
    if (!option) return;

    // Check if it's a default option
    if (option?.isDefault) {
      toast.error("Cannot modify default options status");
      return;
    }

    const confirmToggle = window.confirm(
      `Are you sure you want to ${
        option.isActive ? "deactivate" : "activate"
      } this option?`
    );

    if (confirmToggle) {
      try {
        const response = await get(`QAForm/${rowId}/toggle-status`);

        if (
          response &&
          (response.isSuccess === true || response.status === "success")
        ) {
          toast.success(
            response.message ||
              `Option ${
                option.isActive ? "deactivated" : "activated"
              } successfully!`
          );
          getOptionsData(); // Refresh the list
        } else {
          toast.error(response?.message || "Failed to update option status");
        }
      } catch (error) {
        console.error("Error toggling status:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update option status"
        );
      }
    }
  };

  // Handle question selection
  const handleQuestionSelect = useCallback(
    (question) => {
      setSelectedQuestion(question);
      setSelectedQuestionId(question.id);
      setValue("questionId", question.id.toString(), { shouldValidate: true });

      // Load options for the selected question
      getOptionsData({ questionId: question.id });
    },
    [getOptionsData, setValue]
  );

  // Filter questions by type
  const handleQuestionTypeFilter = useCallback(
    (selectedOption) => {
      const type = selectedOption ? selectedOption.value : "";
      setFilterQuestionType(type);

      if (type === "") {
        setFilteredQuestions(allQuestions);
      } else {
        const filtered = allQuestions.filter(
          (q) => q.questionTypeCode === type
        );
        setFilteredQuestions(filtered);

        // If selected question is not in filtered list, clear selection
        if (selectedQuestion && selectedQuestion.questionTypeCode !== type) {
          setSelectedQuestion(null);
          setSelectedQuestionId(null);
          setValue("questionId", "", { shouldValidate: true });
        }
      }
    },
    [allQuestions, selectedQuestion, setValue]
  );

  // Handle search in table
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    const filter = {};
    if (selectedQuestionId) filter.questionId = selectedQuestionId;
    if (tableFilterQuestionType) filter.questionType = tableFilterQuestionType;
    if (value.trim()) filter.searchTerm = value;

    getOptionsData(filter);
  };

  // Handle table filter by question type
  const handleTableFilter = (selectedOption) => {
    const type = selectedOption ? selectedOption.value : "";
    setTableFilterQuestionType(type);

    const filter = {};
    if (selectedQuestionId) filter.questionId = selectedQuestionId;
    if (type) filter.questionType = type;
    if (searchTerm.trim()) filter.searchTerm = searchTerm;

    getOptionsData(filter);
  };

  // Reset form
  const resetForm = () => {
    reset({
      id: null,
      questionId: "",
      optionText: "",
      optionValue: "",
      displayOrder: 1,
      scoreWeight: 1.0,
      isActive: true,
    });
    setSelectedQuestion(null);
    setSelectedQuestionId(null);
    setFilterQuestionType("");
    getOptionsData();
  };

  // Show all options (reset filters)
  const showAllOptions = () => {
    setSelectedQuestion(null);
    setSelectedQuestionId(null);
    setFilterQuestionType("");
    getOptionsData();
  };

  // Input change handler
  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  // Export to Excel
  const exportToExcel = () => {
    const filteredData = optionsData.map((row, index) => ({
      "Sr No": index + 1,
      "Question ID": row.questionId,
      Question: row.questionText,
      "Question Type": row.questionTypeName || row.questionType,
      Category: row.categoryName,
      "Option Text": row.optionText,
      "Option Value": row.optionValue,
      "Display Order": row.displayOrder,
      "Score Weight": row.scoreWeight,
      Status: row.isActive ? "Active" : "Inactive",
      "Option Type": row.isDefault ? "Default" : "Custom",
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Question Options");
    XLSX.writeFile(workbook, "QuestionOptions.xlsx");
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = selectedQuestion
      ? `Options for: ${selectedQuestion.questionText.substring(0, 50)}${
          selectedQuestion.questionText.length > 50 ? "..." : ""
        }`
      : "Question Options";

    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = 20;
    doc.text(title, titleX, titleY);

    const headers = [
      "Sr No",
      "Question",
      "Question Type",
      "Category",
      "Option Text",
      "Option Value",
      "Display Order",
      "Score Weight",
      "Status",
      "Option Type",
    ];

    const bodyData = optionsData.map((row, index) => [
      index + 1,
      row.questionText?.length > 30
        ? row.questionText.substring(0, 30) + "..."
        : row.questionText || "-",
      row.questionTypeName || row.questionType,
      row.categoryName || "-",
      row.optionText?.length > 20
        ? row.optionText.substring(0, 20) + "..."
        : row.optionText,
      row.optionValue,
      row.displayOrder,
      row.scoreWeight,
      row.isActive ? "Active" : "Inactive",
      row.isDefault ? "Default" : "Custom",
    ]);

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 40 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
        4: { cellWidth: 30 },
        5: { cellWidth: 20 },
        6: { cellWidth: 15 },
        7: { cellWidth: 15 },
        8: { cellWidth: 15 },
        9: { cellWidth: 15 },
      },
    });

    const fileName = selectedQuestion
      ? `Options_Question_${selectedQuestion.id}.pdf`
      : "QuestionOptions.pdf";

    doc.save(fileName);
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 60,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "questionText",
        header: "Question",
        size: 300,
        Cell: ({ row }) => (
          <div
            style={{
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.original.questionText}
          >
            {row.original.questionText}
          </div>
        ),
      },
      {
        accessorKey: "categoryName",
        header: "Category",
        size: 120,
        Cell: ({ row }) => (
          <span className="badge bg-secondary">
            {row.original.categoryName || "-"}
          </span>
        ),
      },
      {
        accessorKey: "questionTypeName",
        header: "Question Type",
        size: 120,
        Cell: ({ row }) => (
          <span className="badge bg-info">
            {row.original.questionTypeName || row.original.questionType}
          </span>
        ),
      },
      {
        accessorKey: "optionText",
        header: "Option Text",
        size: 150,
      },
      {
        accessorKey: "optionValue",
        header: "Option Value",
        size: 120,
      },
      {
        accessorKey: "displayOrder",
        header: "Order",
        size: 80,
      },
      {
        accessorKey: "scoreWeight",
        header: "Score Weight",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.scoreWeight >= 0.7
                ? "bg-success"
                : row.original.scoreWeight >= 0.4
                ? "bg-warning"
                : "bg-danger"
            }`}
          >
            {row.original.scoreWeight.toFixed(1)}
          </span>
        ),
      },
      {
        accessorKey: "isActive",
        header: "Status",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.isActive ? "bg-success" : "bg-danger"
            }`}
          >
            {row.original.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        accessorKey: "isDefault",
        header: "Option Type",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.isDefault ? "bg-info" : "bg-primary"
            }`}
          >
            {row.original.isDefault ? "Default" : "Custom"}
          </span>
        ),
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 140,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original)}
              data-bs-toggle="tooltip"
              title="Edit Option"
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleToggleStatus(row.original.id)}
              data-bs-toggle="tooltip"
              title={row.original.isActive ? "Deactivate" : "Activate"}
              disabled={row.original.isDefault}
            >
              <i
                className={`fas ${
                  row.original.isActive ? "fa-ban" : "fa-check"
                }`}
              ></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDelete(row.original.id)}
              data-bs-toggle="tooltip"
              title="Delete Option"
              disabled={row.original.isDefault || !row.original.canEdit}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    [handleEdit, handleToggleStatus, handleDelete]
  );

  // Fetch data on mount
  useEffect(() => {
    getQuestionTypesData();
  }, []);

  // Load options after questions are loaded
  useEffect(() => {
    if (allQuestions.length > 0) {
      getOptionsData();
    }
  }, [allQuestions]);

  // Initialize tooltips
  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, [optionsData]);

  // Get selected question type value for Select component
  const selectedQuestionTypeValue = useMemo(() => {
    return (
      questionTypes.find((opt) => opt.value === filterQuestionType) || null
    );
  }, [questionTypes, filterQuestionType]);

  // Get selected table filter value
  const selectedTableFilterValue = useMemo(() => {
    return (
      questionTypes.find((opt) => opt.value === tableFilterQuestionType) || null
    );
  }, [questionTypes, tableFilterQuestionType]);

  return (
    <div className="row g-3">
      {/* Left Side - Form */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">
              {getValues("id") ? "Edit Option" : "Add New Option"}
            </h5>
          </div>
          <div className="card-body">
            <form
              className="row g-3 new-form-style"
              onSubmit={handleSubmit(saveOption)}
            >
              <input type="hidden" {...register("id")} />

              <div className="col-md-12">
                <label className="form-label form-label-new">
                  Select Question<span className="text-danger fs-9">*</span>
                </label>

                {/* Question Type Filter */}
                <div className="mb-2">
                  <label className="form-label form-label-new fs-10">
                    Filter by Question Type
                  </label>
                  <Select
                    components={animatedComponents}
                    options={questionTypes}
                    onChange={handleQuestionTypeFilter}
                    value={selectedQuestionTypeValue}
                    placeholder="Select question type..."
                    isClearable
                    className="basic-single"
                    classNamePrefix="select"
                    isLoading={questionsLoading}
                    loadingMessage={() => "Loading question types..."}
                    noOptionsMessage={() => "No question types found"}
                  />
                </div>

                {/* Question Selection */}
                <div
                  className="question-list-container"
                  style={{ maxHeight: "250px", overflowY: "auto" }}
                >
                  {questionsLoading ? (
                    <div className="text-center p-3">
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Loading questions...
                        </span>
                      </div>
                      <small className="text-muted">Loading questions...</small>
                    </div>
                  ) : filteredQuestions.length > 0 ? (
                    filteredQuestions.map((question) => (
                      <div
                        key={question.id}
                        className={`question-item p-2 mb-2 border rounded cursor-pointer ${
                          selectedQuestion?.id === question.id
                            ? "bg-primary text-white"
                            : "bg-light"
                        }`}
                        onClick={() => handleQuestionSelect(question)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong className="d-block mb-1">
                              {question.questionText}
                            </strong>
                            <div className="small d-flex gap-2">
                              <span className="badge bg-secondary">
                                {question.categoryName || "Uncategorized"}
                              </span>
                              <span className="badge bg-info">
                                {question.questionTypeName}
                              </span>
                            </div>
                          </div>
                          {selectedQuestion?.id === question.id && (
                            <i className="fas fa-check"></i>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center p-3 text-muted">
                      {filterQuestionType
                        ? `No questions found for this type`
                        : "No questions available. Create questions in form templates first."}
                    </div>
                  )}
                </div>

                <input
                  type="hidden"
                  {...register("questionId", {
                    required: "Please select a question",
                    validate: (value) =>
                      value !== "" || "Please select a question",
                  })}
                />
                {errors.questionId && (
                  <small className="text-danger d-block mt-1">
                    {errors.questionId.message}
                  </small>
                )}

                {selectedQuestion && (
                  <div className="alert alert-info mt-2 p-2 small">
                    <strong>Selected Question:</strong>{" "}
                    {selectedQuestion.questionText}
                    <br />
                    <small>
                      Type:{" "}
                      <span className="badge bg-info me-2">
                        {selectedQuestion.questionTypeName}
                      </span>
                      • Category:{" "}
                      <span className="badge bg-secondary">
                        {selectedQuestion.categoryName || "Uncategorized"}
                      </span>
                    </small>
                  </div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label form-label-new">
                  Option Text<span className="text-danger fs-9">*</span>
                </label>
                <input
                  {...register("optionText", {
                    required: "Option text is required.",
                    maxLength: {
                      value: 100,
                      message: "Maximum of 100 characters allowed.",
                    },
                  })}
                  type="text"
                  className="form-control"
                  placeholder="Enter option text"
                  onChange={handleChangeOfInputWithLengthCheck("optionText")}
                />
                {errors.optionText && (
                  <small className="text-danger">
                    {errors.optionText.message}
                  </small>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label form-label-new">
                  Option Value<span className="text-danger fs-9">*</span>
                </label>
                <input
                  {...register("optionValue", {
                    required: "Option value is required.",
                    maxLength: {
                      value: 50,
                      message: "Maximum of 50 characters allowed.",
                    },
                  })}
                  type="text"
                  className="form-control"
                  placeholder="e.g., YES, NO, 1, 2, etc."
                  onChange={handleChangeOfInputWithLengthCheck("optionValue")}
                />
                {errors.optionValue && (
                  <small className="text-danger">
                    {errors.optionValue.message}
                  </small>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label form-label-new">
                  Display Order<span className="text-danger fs-9">*</span>
                </label>
                <input
                  {...register("displayOrder", {
                    required: "Display order is required.",
                    valueAsNumber: true,
                    min: { value: 1, message: "Minimum order is 1" },
                    max: { value: 100, message: "Maximum order is 100" },
                  })}
                  type="number"
                  className="form-control"
                  placeholder="1"
                  min="1"
                  max="100"
                />
                {errors.displayOrder && (
                  <small className="text-danger">
                    {errors.displayOrder.message}
                  </small>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label form-label-new">
                  Score Weight<span className="text-danger fs-9">*</span>
                </label>
                <input
                  {...register("scoreWeight", {
                    required: "Score weight is required.",
                    valueAsNumber: true,
                    min: { value: 0, message: "Minimum score is 0" },
                    max: { value: 1, message: "Maximum score is 1" },
                  })}
                  type="number"
                  step="0.1"
                  className="form-control"
                  placeholder="0.0 - 1.0"
                  min="0"
                  max="1"
                />
                {errors.scoreWeight && (
                  <small className="text-danger">
                    {errors.scoreWeight.message}
                  </small>
                )}
              </div>

              <div className="col-md-6">
                <div className="form-check">
                  <input
                    {...register("isActive")}
                    className="form-check-input"
                    type="checkbox"
                    id="isActive"
                    defaultChecked
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Active
                  </label>
                </div>
              </div>

              <div className="col-12 mt-3">
                <button
                  className="btn btn-primary btn-sm"
                  type="submit"
                  disabled={
                    isSubmitting ||
                    loading ||
                    questionsLoading ||
                    !selectedQuestion
                  }
                >
                  {isSubmitting
                    ? "Saving..."
                    : getValues("id")
                    ? "Update Option"
                    : "Add Option"}
                </button>

                {getValues("id") && (
                  <button
                    className="btn btn-secondary btn-sm ms-2"
                    type="button"
                    onClick={resetForm}
                  >
                    Cancel Edit
                  </button>
                )}

                <button
                  className="btn btn-outline-secondary btn-sm ms-2"
                  type="button"
                  onClick={() => {
                    resetForm();
                    toast.info("Form reset");
                  }}
                  disabled={isSubmitting}
                >
                  Reset Form
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right Side - Data Table */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              {selectedQuestion
                ? `Options for: ${selectedQuestion.questionText.substring(
                    0,
                    50
                  )}${selectedQuestion.questionText.length > 50 ? "..." : ""}`
                : "Question Options"}
            </h5>
            <div className="d-flex gap-2">
              {selectedQuestion && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={showAllOptions}
                  title="Show all options"
                  disabled={loading}
                >
                  <i className="fas fa-list me-1"></i> Show All
                </button>
              )}
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={exportToExcel}
                data-bs-toggle="tooltip"
                title="Export to Excel"
                disabled={loading || optionsData.length === 0}
              >
                <i className="fas fa-file-excel me-1"></i> Excel
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={exportToPDF}
                data-bs-toggle="tooltip"
                title="Export to PDF"
                disabled={loading || optionsData.length === 0}
              >
                <i className="fas fa-file-pdf me-1"></i> PDF
              </button>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">
                  {selectedQuestion
                    ? `Loading options for selected question...`
                    : "Loading options..."}
                </p>
              </div>
            ) : optionsData.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                <p className="text-muted">
                  {selectedQuestion
                    ? selectedQuestion.hasOptions
                      ? `No options found for this question.`
                      : `This question type doesn't support options.`
                    : "No options found. Select a question to see its options."}
                </p>
                {!selectedQuestion && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => getOptionsData()}
                    disabled={loading}
                  >
                    <i className="fas fa-sync me-1"></i> Refresh
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <div className="row g-2">
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search options..."
                        value={searchTerm}
                        onChange={handleSearch}
                        disabled={loading}
                      />
                    </div>
                    <div className="col-md-6">
                      <Select
                        components={animatedComponents}
                        options={questionTypes}
                        onChange={handleTableFilter}
                        value={selectedTableFilterValue}
                        placeholder="Filter by question type..."
                        isClearable
                        className="basic-single"
                        classNamePrefix="select"
                        isLoading={questionsLoading}
                        isDisabled={loading}
                      />
                    </div>
                  </div>
                </div>

                <div className="table-container fixed-last-column-table">
                  <CustomDatatableGrid
                    columns={columns}
                    data={optionsData}
                    onExportExcel={exportToExcel}
                    onExportPDF={exportToPDF}
                    initialState={{
                      columnPinning: { right: ["actions"] },
                      columnVisibility: {
                        categoryName: false,
                        questionType: false,
                        optionValue: false,
                        displayOrder: false,
                        isDefault: false,
                      },
                      pagination: {
                        pageSize: 10,
                        pageIndex: 0,
                      },
                    }}
                    enableRowSelection={false}
                    enableColumnFilters={true}
                    enableGlobalFilter={true}
                    enablePagination={true}
                    enableSorting={true}
                    enableColumnResizing={true}
                    muiTablePaperProps={{
                      elevation: 0,
                    }}
                    muiTableContainerProps={{
                      sx: {
                        maxHeight: "500px",
                      },
                    }}
                    renderTopToolbarCustomActions={() => (
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold">
                          Total: {optionsData.length}
                        </span>
                        <span className="badge bg-success">
                          Active: {optionsData.filter((o) => o.isActive).length}
                        </span>
                        <span className="badge bg-info">
                          Default:{" "}
                          {optionsData.filter((o) => o.isDefault).length}
                        </span>
                        <span className="badge bg-primary">
                          Custom:{" "}
                          {optionsData.filter((o) => !o.isDefault).length}
                        </span>
                      </div>
                    )}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OptionsTab;
